/**
 * The day timeline behind screens 06, 07 and 08 (salon-mockup.html timeline()):
 * 1.5 px per minute, one hour line per hour of the working day.
 */
import { useTranslation } from 'react-i18next';

import { serviceName } from '../lib/serviceName';
import { minuteOfDay, minutesToHm, timeToMinutes } from '../lib/time';
import type { Appointment, DayView } from '../types';

const PPM = 1.5;

export function statusClass(a: Appointment): string {
  if (a.overlap_ack) return 'overlap';
  return { booked: '', confirmed: 'confirm', completed: 'done', cancelled: 'cancel', no_show: 'noshow' }[a.status];
}

export function Timeline({
  day,
  onOpen,
  onFree,
}: {
  day: DayView;
  onOpen: (id: string) => void;
  onFree: (startsAt: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const open = day.working_hours ? timeToMinutes(day.working_hours.opens_at) : 9 * 60;
  const close = day.working_hours ? timeToMinutes(day.working_hours.closes_at) : 18 * 60;
  const start = Math.floor(Math.min(open, ...day.appointments.map((a) => minuteOfDay(a.starts_at))) / 60) * 60;
  const endRaw = Math.max(close, ...day.appointments.map((a) => minuteOfDay(a.ends_at) || 24 * 60));
  const end = Math.ceil(endRaw / 60) * 60;
  const pos = (from: number, to: number) => ({ top: (from - start) * PPM, height: Math.max((to - from) * PPM - 3, 14) });
  const hours = [];
  for (let m = start; m <= end; m += 60) hours.push(m);

  return (
    <div className="tl" style={{ height: (end - start) * PPM + 14 }}>
      <div className="tl-hours">
        {hours.map((m) => (
          <div key={m} className="tl-hr" style={{ top: (m - start) * PPM }}>
            <b>{minutesToHm(m)}</b>
          </div>
        ))}
      </div>
      <div className="tl-lane">
        {day.time_off.map((off) => (
          <div key={off.id} className="ap off" style={pos(minuteOfDay(off.starts_at), minuteOfDay(off.ends_at) || 24 * 60)}>
            <span className="apn">{off.reason}</span>
            <span className="apt">
              {minutesToHm(minuteOfDay(off.starts_at))}–{minutesToHm(minuteOfDay(off.ends_at))}
            </span>
          </div>
        ))}
        {day.appointments.map((a) => {
          const from = minuteOfDay(a.starts_at);
          const to = minuteOfDay(a.ends_at) || 24 * 60;
          return (
            <div
              key={a.id}
              className={`ap ${statusClass(a)}`}
              style={{ ...pos(from, to), ...(a.overlap_ack ? { left: '38%' } : {}) }}
              role="button"
              tabIndex={0}
              onClick={() => onOpen(a.id)}
            >
              <span className="apn">{a.client_name}</span>
              <span className="aps">{serviceName(a.service_name_ru, a.service_name_ky, i18n.language)}</span>
              <span className="apt">
                {minutesToHm(from)}–{minutesToHm(to % (24 * 60))}
              </span>
            </div>
          );
        })}
        {day.free_gaps.map((g) => (
          <div
            key={g.starts_at}
            className="ap free"
            style={pos(minuteOfDay(g.starts_at), minuteOfDay(g.ends_at) || 24 * 60)}
            role="button"
            tabIndex={0}
            onClick={() => onFree(g.starts_at)}
          >
            <span>{t('day.freeTap')}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
